import React, { useState } from 'react';
import { ActionWrapperBox } from 'components/Common/common.styled';
import {
  CsvTable,
  ButtonIcon,
  StyledIframe,
  PreviewModal,
  TableScrollX,
  TableScrollY,
  PreviewToolbar,
  PreviewContainer,
  DeleteActionIcon,
  CsvTableContainer,
  TableWrapperOuter,
  RoundedIconCloudDownload,
} from 'components/Ui/styled';
import { fileTypes } from './reportToolComponentConstants';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import PreviewRoundedIcon from '@mui/icons-material/PreviewRounded';
import {
  Box,
  Button,
  DialogContent,
  IconButton,
  Toolbar,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

// export const downloadReportExecutionFile = async (
//   params,
//   getOutputReportExecution,
//   handleErrorResponse,
//   setIsFormLoading
// ) => {
//   const { row } = params;

//   try {
//     setIsFormLoading(true);
//     const scores = await getOutputReportExecution(row.id);
//     const downloadFileType = scores.type;

//     const extension = fileTypes[downloadFileType] || 'bin';

//     const blob = new Blob([scores], { type: downloadFileType });
//     const url = URL.createObjectURL(blob);
//     const link = document.createElement('a');
//     link.href = url;
//     link.setAttribute('download', `report_scores.${extension}`);
//     document.body.appendChild(link);
//     link.click();
//     setIsFormLoading(false);
//   } catch (error) {
//     const blob = error?.response?.data;
//     const errorMessage = await blob?.text();
//     const errorJson = JSON.parse(errorMessage);
//     handleErrorResponse(errorJson.message);
//     setIsFormLoading(false);
//   }
// };

const CsvPreview = ({ data, onDownload, onClose }) => {
  const { lines, blob, extension } = data;

  const handleDownload = () => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${data.reportName}.${extension}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <CsvTableContainer>
      <PreviewToolbar>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            CSV Preview
            <Typography variant="body2" color="text.secondary">
              Total Rows: {lines.length - 1}
            </Typography>
          </Typography>
          <Button onClick={onDownload} variant="text">
            Download CSV
          </Button>
          <IconButton edge="end" onClick={onClose} sx={{ color: 'inherit' }}>
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </PreviewToolbar>
      <TableWrapperOuter>
        <TableScrollX>
          <TableScrollY>
            <CsvTable>
              <thead>
                <tr>
                  {lines[0]?.split(',').map((cell, index) => (
                    <th key={index}>{cell.trim()}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lines.slice(1).map((line, rowIndex) => (
                  <tr key={rowIndex}>
                    {line.split(',').map((cell, cellIndex) => (
                      <td key={cellIndex} title={cell.trim()}>
                        {cell.trim()}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </CsvTable>
          </TableScrollY>
        </TableScrollX>
      </TableWrapperOuter>
    </CsvTableContainer>
  );
};

// File Preview Modal Component
export const FilePreviewModal = ({ open, onClose, previewData, fileType }) => {
  if (!previewData) return null;

  const handleDownload = () => {
    const url = URL.createObjectURL(previewData.blob);
    const link = document.createElement('a');
    link.href = url;

    const downloadFilename =
      previewData.filename ||
      `${previewData.reportName}.${previewData.extension}`;

    link.download = downloadFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleClose = (event, reason) => {
    if (reason === 'backdropClick') {
      return;
    }
    onClose();
  };

  if (fileType === 'csv') {
    return (
      <PreviewModal open={open} onClose={handleClose} maxWidth={false}>
        <CsvPreview
          data={previewData}
          onDownload={handleDownload}
          onClose={onClose}
        />
      </PreviewModal>
    );
  }

  return (
    <PreviewModal open={open} onClose={handleClose} maxWidth={false}>
      <PreviewToolbar>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            File Preview
          </Typography>
          <Button onClick={handleDownload} variant="text">
            Download
          </Button>
          <IconButton edge="end" onClick={onClose} sx={{ color: 'inherit' }}>
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </PreviewToolbar>

      <DialogContent sx={{ p: 0, height: '100%' }}>
        <PreviewContainer>
          <StyledIframe src={previewData.url} />
        </PreviewContainer>
      </DialogContent>
    </PreviewModal>
  );
};

// Custom hook to manage file preview state with data preservation
export const useFilePreview = () => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [fileType, setFileType] = useState('');

  const openPreview = (data, type) => {
    setPreviewData(data);
    setFileType(type);
    setPreviewOpen(true);
  };

  const closePreview = () => {
    setPreviewOpen(false);
  };

  const clearPreview = () => {
    setPreviewOpen(false);
    if (previewData?.url) {
      URL.revokeObjectURL(previewData.url);
    }
    setPreviewData(null);
    setFileType('');
  };

  return {
    previewOpen,
    previewData,
    fileType,
    openPreview,
    closePreview,
    clearPreview,
  };
};

const getFilenameFromContentDisposition = (header) => {
  if (!header) return null;

  console.log('header', header);
  const match = header.match(/filename="?([^"]+)"?/);
  console.log('match', match);

  return match ? match[1] : null;
};

// Updated function - same signature but uses React components internally
export const downloadReportExecutionFile = async (
  params,
  getOutputReportExecution,
  handleErrorResponse,
  setIsFormLoading,
  previewMode = true
) => {
  const { row } = params;
  const { report } = row;

  try {
    setIsFormLoading(true);

    const response = await getOutputReportExecution(row.id);

    const contentDisposition =
      response?.headers?.['content-disposition'] ||
      response?.headers?.['Content-Disposition'] ||
      response?.headers?.get?.('content-disposition') ||
      response?.headers?.get?.('Content-Disposition');

    let blob;
    let downloadFileType;

    if (response.data instanceof Blob) {
      blob = response.data;
      downloadFileType = response.data.type || 'application/pdf';
    } else {
      blob = new Blob([response.data], {
        type: response.data?.type || 'application/pdf',
      });
      downloadFileType = response.data?.type || 'application/pdf';
    }

    console.log('Blob size:', blob.size);
    console.log('Blob type:', blob.type);

    if (blob.size === 0) {
      handleErrorResponse('File is empty');
      setIsFormLoading(false);
      return;
    }

    const fileTypes = {
      'application/pdf': 'pdf',
      'text/csv': 'csv',
      'text/plain': 'txt',
      'image/png': 'png',
      'image/jpeg': 'jpg',
      'image/jpg': 'jpg',
      'image/gif': 'gif',
      'text/html': 'html',
      'application/zip': 'zip',
      'application/x-zip-compressed': 'zip',
      'application/octet-stream': 'bin',
    };

    const extension = fileTypes[downloadFileType] || 'bin';
    const dynamicFileName =
      getFilenameFromContentDisposition(contentDisposition) ||
      `${report}.${extension}`;

    if (previewMode) {
      console.log('Preview Mode: Opening file');

      if (downloadFileType === 'text/csv') {
        const csvText = await blob.text();
        const lines = csvText.split('\n').filter((line) => line.trim());

        setIsFormLoading(false);
        return {
          type: 'csv',
          data: {
            lines,
            blob,
            extension,
            reportName: report,
            filename: dynamicFileName,
          },
        };
      } else {
        const url = URL.createObjectURL(blob);
        const previewableTypes = [
          'application/pdf',
          'image/jpeg',
          'image/png',
          'image/gif',
          'image/jpg',
          'text/plain',
          'text/html',
        ];

        if (previewableTypes.includes(downloadFileType)) {
          setIsFormLoading(false);
          return {
            type: 'preview',
            data: {
              url,
              blob,
              extension,
              fileType: downloadFileType,
              reportName: report,
              filename: dynamicFileName,
            },
          };
        } else {
          // For non-previewable files (like ZIP), download directly with correct filename
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = dynamicFileName;
          link.style.display = 'none';

          document.body.appendChild(link);
          link.click();

          setTimeout(() => {
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
          }, 100);

          setIsFormLoading(false);
          return;
        }
      }
    } else {
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = dynamicFileName;
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      }, 100);

      setIsFormLoading(false);
    }
  } catch (error) {
    console.error('Error:', error);
    handleErrorResponse(error?.message || 'Failed to load file');
    setIsFormLoading(false);
  }
};

export const renderReportToolExecutionAction = (
  params,
  handleDownloadReportExecution,
  handleViewReportExecution,
  handleDeleteReportExecution
) => {
  return (
    <ActionWrapperBox>
      <ButtonIcon
        onClick={() => handleViewReportExecution(params)}
        data-testid="viewButtonIcon"
      >
        <VisibilityRoundedIcon />
      </ButtonIcon>
      <ButtonIcon
        onClick={() => handleDownloadReportExecution(params)}
        data-testid="buttonIcon"
      >
        <PreviewRoundedIcon />
      </ButtonIcon>
      <ButtonIcon
        onClick={() => handleDeleteReportExecution(params)}
        data-testid="deleteButtonIcon"
      >
        <DeleteActionIcon />
      </ButtonIcon>
    </ActionWrapperBox>
  );
};

export const generateReportExecutionDefaultValues = (params) => {
  const defaults = {};
  if (params) {
    Object.entries(params).forEach(([key, field]) => {
      switch (field.fieldType) {
        case 'multiselect':
          defaults[key] = [];
          break;
        case 'select':
          defaults[key] = '';
          break;
        case 'bool':
          defaults[key] = false;
          break;
        case 'text':
        default:
          defaults[key] = '';
          break;
      }
    });
  }
  return defaults;
};
